import { createStoreChunk } from '@core/store'
import type { TPlaylist } from '@core/models'

export type TAudioPlayerState = {
    playlist: TPlaylist
    activeAudioIndex: number
    isPlaying: boolean
    isRepeatEnabled: boolean
    /** 0..1. Persisted (was localStorage in audio-web); applied to the <audio> element by the host. */
    volume: number
    /** HQ source flag. Persisted; drives the source swap in HtmlAudioContainer. */
    isHqEnabled: boolean
    /** First-time HQ info modal shown? Persisted — replaces audio-web's `HAS_SEEN_HQ_MODAL_KEY`. */
    hasSeenHqModal: boolean
    /** Mobile first-view hints shown? Persisted — replaces audio-web's `hasMobilePlayerOpened`. */
    hasSeenMobilePlayerHint: boolean
    lastPlayedAudioId: string | null
    secondPlayCount: number
    isSecondPlayHandled: boolean
}

export const EMPTY_PLAYLIST: TPlaylist = { id: '', items: [], total: 0, pages: 0, page: 0, url: '' }

export const audioPlayerInitialState: TAudioPlayerState = {
    playlist: EMPTY_PLAYLIST,
    activeAudioIndex: 0,
    isPlaying: false,
    isRepeatEnabled: false,
    volume: 1,
    isHqEnabled: false,
    hasSeenHqModal: false,
    hasSeenMobilePlayerHint: false,
    lastPlayedAudioId: null,
    secondPlayCount: 0,
    isSecondPlayHandled: false,
}

export const audioPlayerStore = createStoreChunk<TAudioPlayerState>('audioPlayer')({
    // Numbers/booleans round-trip through the localStorage persist engine (JSON). A field only
    // persists once it differs from its default (volume 1 / isHqEnabled false).
    persist: {
        localStorage: ['volume', 'isHqEnabled', 'hasSeenHqModal', 'hasSeenMobilePlayerHint'],
    },
    initialState: audioPlayerInitialState,
    actions: {
        // Start playback at an index (defaults to the first track). Clears repeat like audio-web.
        playAudio: (activeAudioIndex: number | undefined) => ({
            activeAudioIndex: activeAudioIndex ?? 0,
            isPlaying: true,
            isRepeatEnabled: false,
        }),
        // Overrides the auto-generated setter: switching track clears repeat (single-track loop is
        // per current track, so navigating away from it turns repeat off — audio-web parity).
        setActiveAudioIndex: (activeAudioIndex: number) => ({
            activeAudioIndex,
            isRepeatEnabled: false,
        }),
        resetPlayer: () => ({ isPlaying: false, playlist: EMPTY_PLAYLIST }),
        toggleIsRepeatEnabled: (_: undefined, state: TAudioPlayerState) => ({
            isRepeatEnabled: !state.isRepeatEnabled,
        }),
        toggleIsHqEnabled: (_: undefined, state: TAudioPlayerState) => ({
            isHqEnabled: !state.isHqEnabled,
        }),
        markHqModalSeen: () => ({ hasSeenHqModal: true }),
        markMobilePlayerHintSeen: () => ({ hasSeenMobilePlayerHint: true }),
        incrementSecondPlayCount: (_: undefined, state: TAudioPlayerState) => ({
            secondPlayCount: state.secondPlayCount + 1,
        }),
        markSecondPlayHandled: () => ({ isSecondPlayHandled: true }),
    },
})
